import { Alert, Box, LinearProgress, Typography } from "@mui/material";
import type { FC } from "react";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import ActiveCustomersTable from "./components/ActiveCustomersTable";
import ComparisonPanes from "./components/ComparisonPanes";
import InactiveCustomersTable from "./components/InactiveCustomersTable";
import KpiSections from "./components/KpiSections";
import OrdersBarChart from "./components/OrdersBarChart";
import RegionBreakdownChart from "./components/RegionBreakdownChart";
import ReportFilterPanel from "./components/ReportFilterPanel";
import EurExchangeRateChip from "./components/EurExchangeRateChip";
import ReportHeader from "./components/ReportHeader";
import ReportSubsection from "./components/ReportSubsection";
import SellerMultiSelect from "./components/SellerMultiSelect";
import { summarySections } from "./kpiSections";
import { regionMarginShareItems, regionOrderShareItems } from "./regionChartItems";
import { REPORTS } from "./reportCatalog";
import type { SellerOrdersStatsResponse } from "./statisticsApi";
import { formatActiveFilters } from "./statisticsFormat";
import { useAppliedFilters } from "./useAppliedFilters";
import { useSellerNames } from "./useSellers";
import { useSellerOrdersStatistics } from "./useSellerOrdersStatistics";

interface PaneFilters {
  readonly sellerIds: string[];
  readonly from: string;
  readonly to: string;
}

function defaultPaneAFilters(): PaneFilters {
  return { sellerIds: [], from: dayjs().startOf("month").format(ISO_DATE_FORMAT), to: dayjs().format(ISO_DATE_FORMAT) };
}

function defaultPaneBFilters(): PaneFilters {
  return {
    sellerIds: [],
    from: dayjs().subtract(1, "month").startOf("month").format(ISO_DATE_FORMAT),
    to: dayjs().subtract(1, "month").endOf("month").format(ISO_DATE_FORMAT),
  };
}

interface PaneResultsProps {
  readonly data: SellerOrdersStatsResponse;
}

const PaneResults: FC<PaneResultsProps> = ({ data }) => (
  <>
    <EurExchangeRateChip eurExchangeRate={data.eurExchangeRate} />
    <KpiSections sections={summarySections(data.summary)} />
    <RegionBreakdownChart title="Ilość zamówień — podział na regiony" items={regionOrderShareItems(data.byRegion)} />
    <RegionBreakdownChart title="Średnia marża — podział na regiony" items={regionMarginShareItems(data.byRegion)} />
    <OrdersBarChart title="Ilość zamówień — podział na miesiące" buckets={data.ordersByMonth} granularity="MONTH" />
    <ReportSubsection
      title="Aktywni klienci"
      description="Klienci z co najmniej jednym zamówieniem w wybranym okresie i filtrze sprzedawcy."
    >
      <ActiveCustomersTable rows={data.activeCustomers} />
    </ReportSubsection>
    <ReportSubsection
      title="Nieaktywni klienci"
      description="Klienci ze statusem Active lub Active Lead, którzy nie mają żadnego zamówienia w okresie."
    >
      <InactiveCustomersTable rows={data.inactiveCustomers} />
    </ReportSubsection>
  </>
);

interface PaneProps {
  readonly initialFilters: PaneFilters;
}

const ReportPane: FC<PaneProps> = ({ initialFilters }) => {
  const { draft, applied, isDirty, setDraft, apply } = useAppliedFilters<PaneFilters>(initialFilters);
  const { data, isLoading, error } = useSellerOrdersStatistics(applied.sellerIds, {
    from: applied.from,
    to: applied.to,
  });
  const activeFilters = formatActiveFilters(useSellerNames(applied.sellerIds), applied.from, applied.to);

  return (
    <Box>
      <ReportFilterPanel
        subject={
          <SellerMultiSelect sellerIds={draft.sellerIds} onChange={(sellerIds) => setDraft({ ...draft, sellerIds })} />
        }
        dateLabel="Data rozpoczęcia zamówienia"
        range={draft}
        onRangeChange={({ from, to }) => setDraft({ ...draft, from, to })}
        onApply={apply}
        applyDisabled={!isDirty}
        activeFilters={activeFilters}
      />
      {applied.sellerIds.length === 0 && (
        <Typography sx={{ color: "text.secondary" }}>Wybierz sprzedawców, aby zobaczyć statystyki</Typography>
      )}
      {isLoading && <LinearProgress data-testid="loading-indicator" />}
      {error && <Alert severity="error">Nie udało się pobrać statystyk.</Alert>}
      {data && <PaneResults data={data} />}
    </Box>
  );
};

const SellerOrdersReportPage: FC = () => (
  <Box>
    <ReportHeader report={REPORTS["sellers-orders"]} />
    <ComparisonPanes
      leftLabel="Porównanie A"
      rightLabel="Porównanie B"
      left={<ReportPane initialFilters={defaultPaneAFilters()} />}
      right={<ReportPane initialFilters={defaultPaneBFilters()} />}
    />
  </Box>
);

export default SellerOrdersReportPage;
